// =============================================================================
// domain/goalForm.ts — creating and editing a period target.
// -----------------------------------------------------------------------------
// The rules behind Settings → Goals. `domain/goals.ts` next door MEASURES a
// goal; this file is about the row itself — what a new one starts as, which
// params each template owns, and what may be written back.
//
// ── 🚨 1. `type` IS THE ONE COLUMN WRITTEN WITHOUT BEING READ ──
//
// `goals.type` is NOT NULL and nothing in this client reads it. D62 says never
// send a column you do not own, and the exception is argued rather than assumed:
// the old client never stores an independent value there either — every save
// recomputes it as `template === 'dollar-pl' ? 'dollar' : 'process'`. So it is a
// pure function of `template`, which this form renders and this client reads.
//
// ⚠ AND OMITTING IT WOULD BE THE BUG. Switch a dollar goal to a tag cap without
// writing `type` and the row keeps `'dollar'` beside `template: 'tag-cap'` —
// a contradiction the old client would then carry.
//
// ── 🚨 2. THE PARAMS BLOB IS PER-TEMPLATE, AND SWITCHING TEMPLATE REPLACES IT ──
//
// A `dollar-pl` goal stores `{amount}`; a `tag-cap` stores `{tag, max}`. The old
// client rebuilds the blob from the ACTIVE template's inputs on every save, so
// changing template drops the previous template's keys. That is right — an
// `amount` on a tag cap measures nothing — and it is what `mergeGoalParams`
// does.
//
// ⚠ But when the template is UNCHANGED the prior blob is spread first, so a key
// this client does not render survives the write. The old client drops it; on
// its own data there is never such a key, so this cannot disagree with beta on
// anything beta can produce. It can only be less lossy.
//
// ── 🚨 3. THE FORM KEEPS ONE PARAMS BLOB, NOT ONE PER TEMPLATE ──
//
// Beta's template picker re-renders the inputs but never touches `g.params`, so
// flipping dollar → streak → dollar within one open form still shows the amount
// you typed. Reading each template's keys out of a single blob with beta's own
// fallbacks reproduces that exactly; keeping a blob per template would not.
// =============================================================================

import type { Goal } from './goals';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * The four templates, with the old client's own labels.
 *
 * ⚠ ALL FOUR ARE OFFERED even though this client only MEASURES `dollar-pl`.
 * Hiding the other three would look tidier and would corrupt data: an existing
 * goal whose template is not in the list opens on a `<select>` with no matching
 * option, the browser selects the first, and the next save silently rewrites a
 * tag cap as a dollar target. `measured: false` is what the form says out loud
 * instead.
 */
export const GOAL_TEMPLATES: readonly { value: string; label: string; measured: boolean }[] = [
  { value: 'dollar-pl', label: '$ P&L target over period', measured: true },
  { value: 'boolean-adherence', label: 'Boolean adherence %', measured: false },
  { value: 'tag-cap', label: 'Tag count cap', measured: false },
  { value: 'streak', label: 'Streak / consecutive days', measured: false },
];

/** Periods, in the old client's order. `all` is "All-time". */
export const GOAL_PERIOD_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
  { value: 'all', label: 'All-time' },
];

/** Whether the Goals Progress card can compute a number for this template. */
export const templateIsMeasured = (template: string): boolean =>
  GOAL_TEMPLATES.some((t) => t.value === template && t.measured);

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

export interface GoalDraft {
  name: string;
  template: string;
  period: string;
  accountIds: readonly string[];
  /** ⚠ ONE blob across every template — see note 3. */
  params: Record<string, unknown>;
}

/**
 * A brand-new goal, seeded exactly as the old client seeds it.
 *
 * ⚠ `amount: 500` is beta's, not a guess. A new goal opens on a real number so
 * the shape of the thing is obvious; an empty box reads as "this is optional".
 */
export const newGoalDraft = (): GoalDraft => ({
  name: '',
  template: 'dollar-pl',
  period: 'day',
  accountIds: [],
  params: { amount: 500 },
});

/** An existing goal, opened for editing. */
export function draftFromGoal(g: Goal): GoalDraft {
  return {
    name: g.name ?? '',
    template: g.template || 'dollar-pl',
    period: g.period || 'day',
    accountIds: Array.isArray(g.accountIds) ? [...g.accountIds] : [],
    // ⚠ COPIED, not aliased. The draft is edited in place by the form and the
    // loaded goal must not change under the list behind the modal.
    params: { ...(g.params ?? {}) },
  };
}

/**
 * The name that gets stored.
 *
 * ⚠ Blank becomes "Untitled goal" rather than failing validation, which is the
 * old client's behaviour. A goal is a row in a list, not a metric — refusing to
 * save one because it has no name would be this client inventing a rule.
 */
export const goalStorageName = (name: string): string => name.trim() || 'Untitled goal';

/** `'dollar' | 'process'` — see note 1. */
export const goalType = (template: string): string =>
  (template === 'dollar-pl' ? 'dollar' : 'process');

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

/**
 * The keys a template owns. Anything not listed here is not written by that
 * template's form and therefore not rendered by it either (D61/D62).
 */
export const TEMPLATE_PARAM_KEYS: Readonly<Record<string, readonly string[]>> = {
  'dollar-pl': ['amount'],
  'boolean-adherence': ['booleanKey', 'scope', 'threshold'],
  'tag-cap': ['tag', 'max'],
  streak: ['booleanKey', 'days'],
};

/** The numeric defaults the old client's inputs fall back to. */
const NUMERIC_DEFAULTS: Readonly<Record<string, number>> = {
  amount: 0,
  threshold: 90,
  max: 0,
  days: 5,
};

const NUMERIC_KEYS = new Set(Object.keys(NUMERIC_DEFAULTS));

/**
 * A number box's value: what is stored, or the default when nothing is.
 *
 * ── ⚠ ONE DELIBERATE DIVERGENCE, AND IT IS A BUG FIX ──
 *
 * Beta writes `g.params.threshold || 90`, so a stored **zero** displays as 90.
 * Its inputs are plain DOM and uncontrolled, so that only affects the FIRST
 * render — you can still type 0 and save it.
 *
 * These inputs are controlled off the draft. Reproducing `||` here would make a
 * typed 0 snap straight back to 90 on the next render, so the value could not be
 * entered at all. The fallback therefore applies only when the key is ABSENT or
 * unreadable.
 *
 * ⚠ The one visible difference: a goal already storing `threshold: 0` shows 0
 * here and 90 in the old client. That value is a half-created row rather than a
 * choice, and it is on a template this client does not measure — a worse trade
 * than a form that rejects a keystroke.
 */
export function paramNumber(params: Record<string, unknown>, key: string): number {
  const raw = params?.[key];
  const n = Number(raw);
  if (raw === null || raw === undefined || raw === '' || !Number.isFinite(n)) {
    return NUMERIC_DEFAULTS[key] ?? 0;
  }
  return n;
}

/**
 * A select's current value, or the first option when the stored one is absent.
 *
 * ⚠ THE FALLBACK IS THE BROWSER'S, MADE EXPLICIT. Beta marks an `<option>`
 * selected only when it matches, so a goal pointing at a boolean that was later
 * removed silently lands on whatever renders first — and beta's own migration
 * nulls those references for exactly that reason. Doing it here means the value
 * the user is about to save is the value they can see.
 */
export function paramChoice(
  params: Record<string, unknown>,
  key: string,
  options: readonly string[],
): string {
  const stored = params?.[key];
  if (typeof stored === 'string' && options.includes(stored)) return stored;
  return options[0] ?? '';
}

/**
 * Exactly the active template's keys, coerced.
 *
 * ⚠ An unknown template returns an EMPTY blob rather than passing the draft
 * through. A template this client cannot describe has no owned keys, and
 * writing keys it does not own is the whole thing D62 forbids.
 */
export function ownedParams(
  template: string,
  params: Record<string, unknown>,
  options: { booleanKeys?: readonly string[]; scopes?: readonly string[]; tags?: readonly string[] } = {},
): Record<string, unknown> {
  const keys = TEMPLATE_PARAM_KEYS[template] ?? [];
  const out: Record<string, unknown> = {};

  for (const k of keys) {
    if (NUMERIC_KEYS.has(k)) { out[k] = paramNumber(params, k); continue; }
    if (k === 'booleanKey') { out[k] = paramChoice(params, k, options.booleanKeys ?? []); continue; }
    if (k === 'scope') { out[k] = paramChoice(params, k, options.scopes ?? ['trades', 'days']); continue; }
    if (k === 'tag') { out[k] = paramChoice(params, k, options.tags ?? []); continue; }
    out[k] = params?.[k];
  }
  return out;
}

/**
 * The blob that gets written — see note 2.
 *
 * @param prior the goal as loaded, or null for a new one.
 */
export function mergeGoalParams(
  template: string,
  owned: Record<string, unknown>,
  prior: Goal | null,
): Record<string, unknown> {
  if (prior && prior.template === template) {
    return { ...(prior.params ?? {}), ...owned };
  }
  return owned;
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export interface PickerAccount {
  id: string;
  archived?: boolean;
  excludeFromMetrics?: boolean;
}

/**
 * Live accounts first, then excluded, then archived — the old client's
 * `orderAccountsForPicker`.
 *
 * ⚠ NOT the filter drawer's rule, which folds the same two groups behind a
 * disclosure. Here they stay visible, because a goal may legitimately be tagged
 * to an account that is now archived and hiding it would look like the tag was
 * lost.
 */
export function orderAccountsForPicker<T extends PickerAccount>(
  accounts: readonly T[],
): T[] {
  const active: T[] = []; const excluded: T[] = []; const archived: T[] = [];
  for (const a of accounts) {
    if (a?.archived) archived.push(a);
    else if (a?.excludeFromMetrics) excluded.push(a);
    else active.push(a);
  }
  return [...active, ...excluded, ...archived];
}

/** Tick / untick one account. Order is preserved so the list does not jump. */
export function toggleAccountId(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}
